import { keyBy } from 'lodash';
import { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import { Facility, Organization } from '@crossnokaye/typescript-sdk/models/components';
import {
  ListUserOrgsQueryParamView,
  ListUserOrgsResponse,
} from '@crossnokaye/typescript-sdk/models/operations';

import { useAtlasClient } from '@/hooks/use-atlas-client';
import { useUser } from '@/hooks/use-user';
import { listUserOrgsQuery } from '@/queries/iam/list-user-orgs-query';

export interface UserState {
  facilities: {
    /// Exact list returned by ListUserOrgs.
    unordered: Facility[];
    /// Indexed by facility_id.
    byId: Record<string, Facility>;
    /// Indexed by short_name.
    byShortName: Record<string, Facility>;
    /// Map of agent ID to all related facilities.
    /// Currently 1:1, but that may change in future.
    fromAgent: Record<string, Facility[]>;
    /// Map of facility ID to facility short name which is used in the URL.
    toUrlName: Record<string, string>;
    /// Map of facility short name (which is used in the URL) to facility ID.
    urlToId: Record<string, string>;
  };
  /// Modern view of all the orgs the user has access to.
  organizations: {
    /// Exact list returned by ListUserOrgs.
    unordered: Organization[];
    /// Indexed by organization_id.
    byId: Record<string, Organization>;
    /// Indexed by organization name (URL identifier).
    byName: Record<string, Organization>;
    /// Map of organization ID to organization name which is used in the URL.
    toUrlName: Record<string, string>;
    /// Map of organization name (which is used in the URL) to organization ID.
    urlToId: Record<string, string>;
  };
}

export const transformUserState = (data: ListUserOrgsResponse): UserState => {
  const uou = data.organizationCollection ?? [];
  const orgToUrlName = uou.reduce<Record<string, string>>((acc, org) => {
    if (!org) return acc;
    acc[org.organizationId] = org.name;
    return acc;
  }, {});
  const orgUrlToId = uou.reduce<Record<string, string>>((acc, org) => {
    if (!org) return acc;
    acc[org.name] = org.organizationId;
    return acc;
  }, {});
  const organizations = {
    unordered: uou,
    byId: keyBy(uou, 'organizationId'),
    byName: keyBy(uou, 'name'),
    toUrlName: orgToUrlName,
    urlToId: orgUrlToId,
  };
  const ufu = uou.flatMap((uo) => uo.facilities).filter((f): f is Facility => f !== undefined);
  const ufa: Record<string, Facility[]> = {};
  ufu.forEach((uf) => {
    if (!uf) return;
    uf.agents.forEach((agent) => {
      if (!ufa[agent.agentId]) {
        ufa[agent.agentId] = [];
      }
      ufa[agent.agentId].push(uf);
    });
  });
  const toUrlName = ufu.reduce<Record<string, string>>((acc, uf) => {
    if (!uf) return acc;
    acc[uf.facilityId] = uf.shortName;
    return acc;
  }, {});
  const urlToId = ufu.reduce<Record<string, string>>((acc, uf) => {
    if (!uf) return acc;
    acc[uf.shortName] = uf.facilityId;
    return acc;
  }, {});
  const facilities = {
    unordered: ufu,
    byId: keyBy(ufu, 'facilityId'),
    byShortName: keyBy(ufu, 'shortName'),
    fromAgent: ufa,
    toUrlName: toUrlName,
    urlToId: urlToId,
  };
  return {
    organizations,
    facilities,
  };
};

export const useUserState = (): UseQueryResult<UserState | undefined> => {
  const { userId } = useUser();
  const client = useAtlasClient();

  return useQuery(
    listUserOrgsQuery({
      userId,
      view: ListUserOrgsQueryParamView.FacilitiesExtended,
      transform: transformUserState,
      client,
    })
  );
};
